import { useCallback, useMemo, useState } from 'react'

import { ExternalLink } from 'lucide-react'

import { t } from '../../../copy'
import { PJ } from '../../../copy/pjshell-text'
import { useMe, useProject } from '../../../app/queries'
import { LuluView } from '../../lulu/view'
import { HiddenAttachmentInput } from '../../lulu/attachment-input'
import {
  conversationCreateKeys,
  releaseCreateConversationKey,
  type ConversationCreateIntent,
} from '../../lulu/conversation-create-key'
import { createLuluConversation, useProjectConversations } from '../../lulu/queries'
import { useConversationWorkbench } from '../../lulu/use-conversation-workbench'
import { useSidecarLayout } from '../../lulu/use-sidecar-layout'
import { useProjectId } from './params'
import {
  ModuleError,
  ModuleHeader,
  ModulePage,
  ModuleSkeleton,
  luluPrimaryButtonClass,
  moduleTitle,
} from './ui'

/**
 * 项目详情下的「对话」页。
 *
 * # 它不是第二套聊天
 *
 * 设计稿 pjChat 的原话：「这是简版，**不是第二套聊天**……和公共 Lulu 是
 * 同一个内核，只是把项目固定住了」。
 *
 * 2026-08-30 之前这一屏自己写了一份消息列表、一份输入框、一份「安排开发」。
 * 那之后它改成复用 `ChatColumn`，**但接线还是自己抄了一份**——于是到
 * 2026-09-08 已经分家三处：只 @ 人也进等待态、revision 只刷三份投影、
 * `onOpenSidecar` 是空函数（「团队」那颗按钮点了没反应，没有团队面板、
 * 审批、看板、计划）。arron：「项目里的会话，里面的团队要和外面 Lulu
 * 对话保持一致」。
 *
 * # 现在的做法
 *
 * 与 /lulu 共用同一份 `useConversationWorkbench`（一条会话的全部取数与动作）
 * 与 `useSidecarLayout`（Sidecar 的开合与轨道），渲染同一个 `LuluView`。
 * 这一屏自己只剩三件事：**项目固定**（`projectId` 来自路由，会话只在这个
 * 项目下列、新建也建在这个项目下）、本地选中哪一条、外壳与标题。
 * 会话导航交给项目页自己（`conversationNavigationExternal`），
 * 所以 LuluView 不画左栏。
 */
export function ProjectConversationPage() {
  const projectId = useProjectId()
  if (!projectId) {
    return (
      <ModulePage testid="project-module-conversation">
        <ModuleSkeleton />
      </ModulePage>
    )
  }
  // **按项目换实例。** selectedId 是这一屏的本地状态；不换 key 的话，
  // 从上一个项目点「新建」留下的 id 会跟着进下一个项目。
  return <Loaded key={projectId} projectId={projectId} />
}

// 路由那侧用的是这个名字（router.tsx 从 ./conversations 转出去）。
export { ProjectConversationPage as ProjectConversations }

function Loaded({ projectId }: { readonly projectId: string }) {
  const creatorUserId = useMe().data?.id
  const project = useProject(projectId)
  // 这个项目下的会话。**用 lulu 那条按项目问的查询**，不另写一份。
  const conversationLists = useProjectConversations([projectId])
  const list = conversationLists[0]
  const items = useMemo(() => list?.data?.conversations ?? [], [list?.data])

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  // 只认还在这个项目列表里的 id。新建之后列表还没刷回来时，
  // selectedId 可能暂时不在 items 里——那一拍仍用它，否则刚建完会闪回旧会话。
  const current = useMemo(() => {
    if (selectedId !== undefined) {
      if (items.length === 0 || items.some((c) => c.id === selectedId)) return selectedId
    }
    return items[0]?.id
  }, [selectedId, items])

  // **与 /lulu 同一份接线。** 差别只有两个参数：Agent 库看这个项目那一层
  // （服务端会把账户级也带上），改名之后重取的是这个项目的列表。
  const wb = useConversationWorkbench({
    conversationId: current,
    agentScope: { scope: 'project', projectId },
    onRenamed: () => Promise.resolve(list?.refetch()),
  })
  const { stream, snapshot, attach, filePickerRef } = wb
  const { measureRef, tracks, sidecarTab, openSidecar, closeSidecar, resizeSidecar } =
    useSidecarLayout()

  const [creating, setCreating] = useState(false)
  const createHere = useCallback(() => {
    if (!creatorUserId) return
    setCreating(true)
    const title = t('chat.list.create')
    const intent: ConversationCreateIntent = {
      creatorUserId,
      projectId,
      mode: 'solo',
      title,
    }
    const key = conversationCreateKeys.keyFor(intent)
    void (async () => {
      try {
        const created = await createLuluConversation({
          title,
          projectId,
          mode: 'solo',
          idempotencyKey: key,
        })
        releaseCreateConversationKey(conversationCreateKeys, intent, key, 201, true)
        setSelectedId(created.id)
        await list?.refetch()
      } catch (err) {
        const status = (err as { status?: number } | null)?.status
        releaseCreateConversationKey(conversationCreateKeys, intent, key, status, false)
      } finally {
        setCreating(false)
      }
    })()
  }, [creatorUserId, projectId, list])

  const name = project.data?.name ?? t('nav.project.loading')
  const fullHref = current ? `/lulu/c/${current}` : '/lulu'

  return (
    <ModulePage testid="project-module-conversation" layout="fill">
      {/* composer-pin-v2: LuluFrame absolute inset pin — flex-only fill still raced after data load */}
      <HiddenAttachmentInput inputRef={filePickerRef} onPick={attach.add} />
      <div className="shrink-0 [&_header]:mb-3">
        <ModuleHeader
          title={moduleTitle(name, t('nav.ptab.chat'))}
          subtitle={<PJ id="pjshell.chat.subtitle" />}
          actions={
            /*
              **去 /lulu，而且带上这条会话。** 没有会话时退到 /lulu 让他自己挑，
              不拼一个假的会话 id。
            */
            <a href={fullHref} className={luluPrimaryButtonClass} data-testid="open-in-lulu">
              <PJ id="pjshell.chat.open_full" />
              <span className="pj-btn-aff" aria-hidden="true">
                <ExternalLink size={12} strokeWidth={1.6} />
              </span>
            </a>
          }
        />
      </div>
      {list?.error || wb.messages.error ? (
        <ModuleError message={(list?.error ?? wb.messages.error)!.message} />
      ) : null}

      {/* Soft Structuralism embed shell: 16 radius + double-bezel; context bar radius 12.
          Same height contract as LuluFrame (absolute inset-0): shell min-h-screen flex
          ancestors grow with message/status content after load, so flex-1 + overflow-hidden
          alone still clipped the composer once banners/messages settled. Absolute pin makes
          the inner column ignore content height; only the message scroller overflows. */}
      <div className="pj-chat-shell relative min-h-0 flex-1 overflow-hidden">
        <div className="absolute inset-0 flex flex-col" data-testid="pj-chat-pin">
          <div className="pj-chat-context flex shrink-0 items-center gap-npc-gap-sm px-npc-grid-sm py-npc-gap-sm">
            <span className="min-w-0 flex-1 truncate text-npc-caption-sm text-npc-lulu-t3">
              {project.data ? (
                <PJ id="pjshell.chat.context" vars={{ name: project.data.name }} />
              ) : (
                <PJ id="pjshell.chat.current" />
              )}
            </span>
            <a
              href={fullHref}
              className="pj-chat-context-link inline-flex h-7 shrink-0 items-center rounded-full border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2.5 text-npc-meta font-medium text-npc-lulu-t2"
              data-testid="open-in-lulu-panel"
            >
              <PJ id="pjshell.chat.open_full" />
            </a>
          </div>

          {/* measureRef 量的是这一格的宽：Sidecar 是轨道还是抽屉由它定，与 /lulu 同一条规则。 */}
          <div
            ref={measureRef}
            data-testid="pjshell-chat-messages"
            className="pj-chat-embed lulu-theme flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            {list?.isPending ? (
              <ModuleSkeleton />
            ) : !current ? (
              /*
                这个项目下还没有会话。**建一条就是了**——不画一个空的聊天框
                让他对着一个发不出去的输入框。
              */
              <div className="flex flex-col gap-npc-gap-sm p-npc-grid-sm">
                <span className="text-npc-caption text-npc-lulu-t3">{t('chat.message.empty')}</span>
                <button
                  type="button"
                  data-testid="pjshell-chat-create"
                  className={luluPrimaryButtonClass}
                  disabled={creating}
                  onClick={createHere}
                >
                  {t('chat.list.create')}
                </button>
              </div>
            ) : (
              <LuluView
                runSteps={stream.steps}
                snapshot={snapshot}
                team={wb.team}
                teamCandidates={wb.teamCandidates}
                people={wb.people}
                plan={wb.plan}
                board={wb.boardNodes}
                decisions={wb.pendingDecisions}
                /*
                  绑定卡在这一屏永远不会出：snapshot.projectId 恒非空（会话是建在
                  这个项目下的）。**所以 bindTargets / onBindProject 不给**——
                  给一个走不到的回调，下一个人会以为这一屏能改绑，而绑定是单向的。
                */
                invitablePeople={wb.invitablePeople}
                onInvitePerson={wb.invitePerson}
                onRemovePerson={wb.removePerson}
                onJoinTeam={wb.joinTeam}
                onRetrySidecar={wb.retrySidecar}
                goalPlan={wb.goalPlan.isPending ? undefined : (wb.goalPlan.data ?? null)}
                justScheduledTaskId={wb.justScheduledTaskId}
                attachments={attach.items}
                onPickAttachment={() => filePickerRef.current?.click()}
                onRetryAttachment={attach.retry}
                onRemoveAttachment={attach.remove}
                attachmentNotice={attach.notice}
                onStartWork={wb.startWork}
                startingWork={wb.startingWork}
                startWorkFailed={wb.startWorkFailed}
                composerModel={wb.composerModel.data?.composer_model ?? null}
                onSendMessage={wb.sendMessage}
                onAnswerDecision={wb.answer}
                onRecoverSubrun={wb.recover}
                onUndoTask={wb.undoTask}
                onCancelSubrun={wb.cancelSubrun}
                onStopRound={wb.stopRound}
                paused={wb.paused}
                onPause={wb.archived ? undefined : wb.pause}
                onResume={wb.archived ? undefined : wb.resume}
                skills={wb.skills.data?.skills ?? (wb.skills.isError ? null : undefined)}
                onRenameConversation={wb.rename}
                models={wb.agentModelOptions}
                onPickModel={wb.pickModel}
                // 会话导航是项目页的事：左栏不画，列表也不用给。
                railGroups={[]}
                thinkingSeconds={wb.thinkingSeconds}
                messages={wb.messages.data?.messages ?? []}
                participants={wb.participantsById}
                tracks={tracks}
                sidecarTab={sidecarTab}
                onOpenSidecar={openSidecar}
                onCloseSidecar={closeSidecar}
                onSelectConversation={setSelectedId}
                onNewConversation={createHere}
                onResizeSidecar={resizeSidecar}
                conversationNavigationExternal
              />
            )}
          </div>
        </div>
      </div>
    </ModulePage>
  )
}
